import { useCallback, useEffect, useState } from 'react'
import { AppHTTPService } from '../../../api/http-service'
import { useActiveEnvironment } from '../../../hooks/useEnvironments'
import { spacesUrl } from '../../../utils/pageUtils'
import { useIdentity } from '../../../hooks/useIdentity'
import { AutobuildLoadingScreen } from './AutobuildLoadingScreen'
import { CustomErrorComponent } from '../../../components/Shared/CustomErrorComponent'

interface SpaceRedirectProps {
  spaceId: string
  environmentId?: string
}

export const SpaceRedirect = ({ spaceId, environmentId }: SpaceRedirectProps) => {
  const { environment } = useActiveEnvironment()
  const { identity } = useIdentity()
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchAccessToken = useCallback(async () => {
    try {
      setIsLoading(true)
      const { data } = await AppHTTPService.client.auth.getApiKeys({
        environmentId: environmentId || environment.id,
      })
      const token = data.find((key) => key.type === 'SECRET')?.rawKey
      if (token) {
        setAccessToken(token)
      } else {
        setError('No access token found')
      }
      setIsLoading(false)
    } catch (err) {
      setError('Error accessing credentials')
      setIsLoading(false)
    }
  }, [environment.id, environmentId])

  useEffect(() => {
    if (!accessToken && !error) {
      fetchAccessToken()
    }
  }, [fetchAccessToken, accessToken, error])

  // Navigate directly to the space URL once we have the access token
  useEffect(() => {
    if (accessToken && !error && !isLoading) {
      const spaceUrl = spacesUrl(spaceId, identity.token, undefined, undefined, 'assistant')
      // Navigate directly to the space URL instead of embedding in iframe
      window.location.href = spaceUrl
    }
  }, [accessToken, error, isLoading, spaceId, identity.token])

  if (error) {
    return (
      <CustomErrorComponent
        page='embedded-space'
        message={`Unable to load the workspace: ${error}`}
        title='Error Loading Space'
        retryButtonText='Try Again'
        onRetryClick={() => {
          setError(null)
          setIsLoading(true)
          fetchAccessToken()
        }}
      />
    )
  }

  // Show loading screen while fetching access token or navigating
  return <AutobuildLoadingScreen stage='loading-space' />
}
